<script setup lang="ts">
import { nextTick, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { ShieldCheck, X } from 'lucide-vue-next'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import PageHeader from '@lib/components/shared/PageHeader.vue'
import BBtn from '@lib/components/base/BBtn.vue'
import { useUiStore } from '@/stores/ui'
import { useTicketVerify } from '@/composables/useTicketVerify'
import TicketVerifyResult from '@/components/domain/tickets/TicketVerifyResult.vue'

const { t } = useI18n()
const route = useRoute()
const ui = useUiStore()
const { loading, error, result, lastCode, load, reset } = useTicketVerify()

const code = ref('')
const inputRef = ref<HTMLInputElement | null>(null)

function focusInput() {
    void nextTick(() => inputRef.value?.focus())
}

function submit() {
    const c = code.value.trim()
    if (!c) return
    void load(c)
}

function clearCode() {
    reset()
    code.value = ''
    focusInput()
}

onMounted(() => {
    ui.setBreadcrumbs([{ label: t('nav.tickets'), to: '/tickets' }, { label: t('nav.verifyTicket') }])
    const q = route.query.code
    const initial = Array.isArray(q) ? q[0] : q
    if (initial) {
        code.value = String(initial)
        void load(code.value)
    } else {
        focusInput()
    }
})
</script>

<template>
    <DefaultLayout>
        <PageHeader :title="t('tickets.verify.title')" :subtitle="t('tickets.verify.subtitle')" />

        <div class="max-w-2xl space-y-4">
            <form class="flex items-end gap-2" @submit.prevent="submit">
                <label class="flex-1">
                    <span class="block text-sm text-gray-500 mb-1">{{ t('tickets.verify.enterCode') }}</span>
                    <div class="relative">
                        <input
                            ref="inputRef"
                            v-model="code"
                            type="text"
                            autocomplete="off"
                            autocapitalize="none"
                            autocorrect="off"
                            class="w-full h-10 text-base pl-3 pr-9 rounded-6 border border-gray-200 bg-white focus:border-teal focus:ring-2 focus:ring-teal/20 focus:outline-none"
                        />
                        <button
                            v-if="code.length"
                            type="button"
                            :aria-label="t('common.actions.clear')"
                            :title="t('common.actions.clear')"
                            class="absolute right-1.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-6 flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                            @click="clearCode"
                        >
                            <X class="w-4 h-4" />
                        </button>
                    </div>
                </label>
                <BBtn type="submit" size="large" :disabled="!code.trim().length || loading">
                    <ShieldCheck class="w-4 h-4" />
                    {{ t('tickets.verify.verify') }}
                </BBtn>
            </form>

            <div v-if="loading" class="flex items-center gap-2 text-sm text-gray-500">
                <span class="w-4 h-4 border-2 border-teal border-t-transparent rounded-full animate-spin" />
                {{ t('tickets.verify.verify') }}…
            </div>

            <div v-else-if="error" class="bg-red-l text-red rounded-12 px-4 py-3 text-center font-medium">
                {{ t('tickets.verify.error') }}
                <div v-if="lastCode" class="text-sm mt-1 font-mono">{{ lastCode }}</div>
            </div>

            <div v-else-if="result" class="space-y-3">
                <div class="text-center font-mono text-sm text-gray-500">{{ lastCode }}</div>
                <TicketVerifyResult :data="result" />
            </div>
        </div>
    </DefaultLayout>
</template>
